import * as React from 'react';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import CheckIcon from '../icons/check';

const checkboxVariants = cva(
  'peer shrink-0 rounded-sm border-2 border-grey-4 bg-white ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-default-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-default-blue data-[state=checked]:bg-default-blue data-[state=checked]:text-white align-middle',
  {
    variants: {
      size: {
        sm: 'size-3',
        md: 'size-[14px]', // Default
        lg: 'size-4',
        xl: 'size-5',
        '2xl': 'size-[26px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

const indicatorVariants = cva('absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2', {
  variants: {
    size: {
      sm: 'size-2.5',
      md: 'size-3.5',
      lg: 'size-4',
      xl: 'size-4.5',
      '2xl': 'size-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(({ className, size, ...props }, ref) => (
  <CheckboxPrimitive.Root ref={ref} className={cn(checkboxVariants({ size }), className)} {...props}>
    <CheckboxPrimitive.Indicator className={cn('relative flex items-center justify-center text-current')}>
      <CheckIcon className={cn(indicatorVariants({ size }))} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
